// src/page/VoucherEdit.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

export default function VoucherEdit() {
  useAuthGuard();
  const navigate = useNavigate();
  const { voucherID } = useParams();

  // ---------- helpers ----------
  const pad = (n) => String(n).padStart(2, '0');
  const fmtDate = (d) =>
    `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;

  // ---------- states ----------
  const [voucherName, setVoucherName] = useState('');
  const [voucherUsage, setVoucherUsage] = useState('0'); // "0":目視, "1":QR

  // validity (UI)
  const [validType, setValidType] = useState('range'); // 'range' | 'days'
  const [validRange, setValidRange] = useState('');
  const [validDays, setValidDays] = useState('1');

  // texts
  const [howToUse, setHowToUse] = useState('');
  const [termsOfUse, setTermsOfUse] = useState('');
  const [caution, setCaution] = useState('');

  // spots
  const [spotId, setSpotId] = useState('');
  const [spots, setSpots] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(false);

  // meta
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // pickers
  const validFpRef = useRef(null);

  // flatpickr for valid range
  useEffect(() => {
    if (!window.flatpickr) return;
    validFpRef.current = window.flatpickr('#public_range', {
      mode: 'range',
      enableTime: true,
      time_24hr: true,
      dateFormat: 'Y/m/d H:i',
      locale: window.flatpickr.l10ns?.ja || 'ja',
      onClose: (selectedDates) => {
        if (selectedDates.length === 2) {
          const [s, e] = selectedDates;
          setValidRange(`${fmtDate(s)} ~ ${fmtDate(e)}`);
        }
      }
    });
    return () => { try { validFpRef.current?.destroy(); } catch { } };
  }, []);

  // reflect controlled -> picker
  useEffect(() => {
    if (validFpRef.current && typeof validRange === 'string' && validRange.includes('~')) {
      const [a, b] = validRange.split('~').map(s => s.trim());
      if (a && b) validFpRef.current.setDate([a, b], true);
    }
  }, [validRange]);

  // load spots
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingSpots(true);
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
          headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
          signal: ac.signal
        });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
        const mapped = list.map(s => ({
          spotId: s.spotId ?? s.id ?? s.spot_id ?? '',
          spotName: s.spotName ?? s.name ?? s.spot_name ?? ''
        })).filter(x => x.spotId && x.spotName);
        setSpots(mapped);
      } catch { setSpots([]); }
      finally { setLoadingSpots(false); }
    })();
    return () => ac.abort();
  }, []);

  // load voucher detail (prefill)
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${API_BASE_URL}/v1/voucher/${voucherID}`, {
          headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const v = data?.voucher || data || {};

        setVoucherName(v.voucherName || v.voucher_name || '');
        setVoucherUsage(((v.usageType ?? v.usage_type ?? '0')) + '');
        setSpotId(v.spotId ?? v.spot_id ?? v.storeId ?? v.spot?.spotId ?? '');

        // validity
        const startISO = v.effectivePeriodStartDate ?? v.effective_period_start_date;
        const endISO = v.effectivePeriodEndDate ?? v.effective_period_end_date;
        const daysVal = (v.effectiveDays ?? v.effective_days);

        if (Number.isFinite(daysVal)) {
          setValidType('days');
          setValidDays(String(daysVal));
          setValidRange('');
        } else if (startISO && endISO) {
          const s = new Date(startISO);
          const e = new Date(endISO);
          if (!isNaN(s) && !isNaN(e)) {
            setValidType('range');
            setValidRange(`${fmtDate(s)} ~ ${fmtDate(e)}`);
          } else {
            setValidType('range');
            setValidRange('');
          }
        } else {
          setValidType('range');
          setValidRange('');
        }

        // texts
        setHowToUse(v.howToUse ?? v.how_to_use ?? '');
        setTermsOfUse(v.termsOfUse ?? v.terms_of_use ?? '');
        setCaution(v.caution ?? v.caution_text ?? v.caution_note ?? '');
      } catch (e) {
        if (e.name !== 'AbortError') setError('読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [voucherID, API_BASE_URL]);

  // submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const payload = {
      voucherName: (voucherName ?? '').trim() || undefined,
      spotId: spotId || undefined,
      usageType: voucherUsage || undefined,
      howToUse: (howToUse ?? '').trim() || '',       // allow clearing
      termsOfUse: (termsOfUse ?? '').trim() || '',
      caution: (caution ?? '').trim() || '',
    };

    // send validity (so API can persist)
    if (validType === 'days') {
      const n = parseInt(validDays, 10);
      if (Number.isFinite(n) && n > 0) {
        payload.validType = 'days';
        payload.validDays = n;
      }
    } else if (validType === 'range' && typeof validRange === 'string' && validRange.includes('~')) {
      payload.validType = 'range';
      payload.validRange = validRange;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE_URL}/v1/voucher/${voucherID}`, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        let msg = `更新に失敗しました (HTTP ${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
        throw new Error(msg);
      }
      alert('更新しました。');
      navigate('/voucher');
    } catch (e) {
      alert(e.message || '更新に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <>
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><a href="/voucher">デジチケ管理</a></li>
            <li className="breadcrumb-item active">編集</li>
          </ol>
        </nav>

        <form className="mb-9" onSubmit={handleSubmit}>
          <div className="row g-3 flex-between-end mb-5">
            <div className="col-auto">
              <h2 className="mb-2">バウチャー編集</h2>
              <h5 className="text-body-tertiary fw-semibold"></h5>
            </div>
            <div className="col-auto">
              <button className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0" type="button"
                onClick={() => window.history.back()} disabled={saving}>キャンセル</button>
              <button className="btn btn-primary mb-2 mb-sm-0" type="submit" disabled={saving}>
                {saving ? '保存中...' : '更新'}
              </button>
            </div>
          </div>

          <div className="row g-5">
            <div className="col-12 col-xl-8">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">バウチャーID</h4>
                    <input className="form-control" type="text" disabled value={voucherID} />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">バウチャー名<span className="text-danger">*</span></h4>
                <input className="form-control" type="text" value={voucherName}
                  onChange={(e) => setVoucherName(e.target.value)} />
              </div>

              <div className="mb-4">
                <h4 className="mb-3">スポット名<span className="text-danger">*</span></h4>
                <div className="input-group">
                  <select
                    className="form-control"
                    value={spotId}
                    onChange={(e) => setSpotId(e.target.value)}
                    disabled={loadingSpots}
                    required
                  >
                    <option value="">{loadingSpots ? '読み込み中...' : '選択してください'}</option>
                    {spots.map(s => (
                      <option key={s.spotId} value={s.spotId}>{s.spotName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">有効期間</h4>

                    {validType === 'range' && (
                      <div id="period_range_area" className="mt-2">
                        <input className="form-control" type="text" id="public_range"
                          placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                          value={validRange} onChange={(e) => setValidRange(e.target.value)} />
                      </div>
                    )}

                    {validType === 'days' && (
                      <div id="days_from_purchase_area" className="mt-2">
                        <div className="input-group">
                          <input type="number" className="form-control" min="1"
                            value={validDays} onChange={(e) => setValidDays(e.target.value)} />
                          <span className="input-group-text">日間有効</span>
                        </div>
                      </div>
                    )}

                    <div className="form-check form-check-inline mt-2">
                      <input className="form-check-input" type="radio" id="valid_range"
                        value="range" checked={validType === 'range'}
                        onChange={(e) => setValidType(e.target.value)} />
                      <label className="form-check-label" htmlFor="valid_range">期間選択</label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input className="form-check-input" type="radio" id="valid_days"
                        value="days" checked={validType === 'days'}
                        onChange={(e) => setValidType(e.target.value)} />
                      <label className="form-check-label" htmlFor="valid_days">購入日より</label>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-4">
                    <h4 className="mb-3">チケット利用区分</h4>
                    <div className="d-flex align-items-center gap-3">
                      <label className="mb-0">
                        <input type="radio" name="voucher_usage" value="0"
                          checked={voucherUsage === '0'} onChange={() => setVoucherUsage('0')} /> 目視確認
                      </label>
                      <label className="mb-0">
                        <input type="radio" name="voucher_usage" value="1"
                          checked={voucherUsage === '1'} onChange={() => setVoucherUsage('1')} /> QR表示
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="mb-3">使用方法</h4>
                <textarea className="form-control" rows={4}
                  value={howToUse} onChange={(e) => setHowToUse(e.target.value)} />
              </div>
              <div className="mb-4">
                <h4 className="mb-3">利用条件</h4>
                <textarea className="form-control" rows={4}
                  value={termsOfUse} onChange={(e) => setTermsOfUse(e.target.value)} />
              </div>
              <div className="mb-4">
                <h4 className="mb-3">注意事項</h4>
                <textarea className="form-control" rows={4}
                  value={caution} onChange={(e) => setCaution(e.target.value)} />
              </div>
            </div>

            {/* Right column (UI only) */}
            <div className="col-12 col-xl-4">
              <div className="row g-2">

                {/* image block (UI only) */}
                <div className="col-12 col-xl-12">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="card-title mb-4">バウチャー画像</h4>
                      <div className="dropzone dropzone-multiple p-0 mb-5" id="my-awesome-dropzone" data-dropzone="data-dropzone">
                        <div className="fallback"><input name="file" type="file" multiple /></div>
                        <div className="dz-preview d-flex flex-wrap" />
                        <div className="dz-message text-body-tertiary text-opacity-85" data-dz-message="data-dz-message">
                          Drag your photo here<span className="text-body-secondary px-1">or</span>
                          <button className="btn btn-link p-0" type="button">Browse from device</button><br />
                          <img className="mt-3 me-2" src="../../../assets/img/icons/image-icon.png" width="40" alt="" />
                        </div>
                      </div>
                      {loading && <div className="text-body-secondary mt-3">読み込み中...</div>}
                      {!loading && error && <div className="text-danger mt-3">{error}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </>
    </Layout>
  );
}
